"use client";

import {
  useTheme,
  useMediaQuery,
  Typography,
  Grid,
  Collapse,
} from "@mui/material";
import { pxToVw } from "@/utils/pxToVw";
import { MOBILE_BASE } from "@/theme/theme";
import { useState } from "react";
import { useText } from "@/context/TextContext";

export default function FaqsSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [expandedBlocks, setExpandedBlocks] = useState<{
    [key: number]: boolean;
  }>({});

  const handleToggle = (index: number) => {
    setExpandedBlocks((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const faqs = [
    {
      title: useText("block.faqs.faq1.title"),
      description: useText("block.faqs.faq1.text"),
    },
    {
      title: useText("block.faqs.faq2.title"),
      description: useText("block.faqs.faq2.text"),
    },
    {
      title: useText("block.faqs.faq3.title"),
      description: useText("block.faqs.faq3.text"),
    },
    {
      title: useText("block.faqs.faq4.title"),
      description: useText("block.faqs.faq4.text"),
    },
    {
      title: useText("block.faqs.faq5.title"),
      description: useText("block.faqs.faq5.text"),
    },
    {
      title: useText("block.faqs.faq6.title"),
      description: useText("block.faqs.faq6.text"),
    },
  ];

  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "");

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs
      .filter((faq) => faq.title && faq.description)
      .map((faq) => ({
        "@type": "Question",
        name: faq.title,
        acceptedAnswer: {
          "@type": "Answer",
          text: stripHtml(faq.description),
        },
      })),
  };

  return (
    <>
    {/* JSON-LD de FAQPage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema),
        }}
      />
      <Grid
        container
        mt={{ xs: pxToVw(30, MOBILE_BASE), md: pxToVw(40) }}
        mb={{ xs: pxToVw(80, MOBILE_BASE), md: pxToVw(160) }}
        spacing={{ xs: pxToVw(30, MOBILE_BASE), md: pxToVw(8) }}
      >
        <Grid
          container
          size={3}
          borderTop={{ xs: "none", md: "1px solid #472600" }}
          borderBottom={{ xs: "none", md: "1px solid #472600" }}
          pt={pxToVw(10)}
        >
          <Typography variant="titulo" color="marron" component="h2">
            {useText("block.faqs.title")}
          </Typography>
        </Grid>
        <Grid
          size={{ xs: 12, md: 9 }}
          columns={{ xs: 12, md: 9 }}
          container
          spacing={{ xs: pxToVw(30, MOBILE_BASE), md: pxToVw(48) }}
          pb={{ xs: pxToVw(30, MOBILE_BASE), md: pxToVw(48) }}
          borderBottom={"1px solid #472600"}
        >
          {faqs.map((faq, index) => {
            const isExpanded = expandedBlocks[index] || false;

            return (
              <Grid
                key={index}
                size={{ xs: 12, md: 9 }}
                columns={{ xs: 12, md: 9 }}
                paddingTop={{ xs: pxToVw(10, MOBILE_BASE), md: pxToVw(10) }}
                borderTop={"1px solid #472600"}
                container
                spacing={{ xs: pxToVw(12, MOBILE_BASE), md: pxToVw(8) }}
                onClick={() => handleToggle(index)}
              >
                <Grid size={{ xs: 10, md: 8 }} container>
                  <Typography
                    variant="label1"
                    fontSize={{ xs: pxToVw(18, MOBILE_BASE), md: pxToVw(16) }}
                    lineHeight={{ xs: "110%", md: "120%" }}
                    color="marron"
                    component="h3"
                    sx={{ textTransform: "uppercase", cursor: "pointer" }}
                  >
                    {faq.title}
                  </Typography>
                  {faq.description && !isMobile && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Typography
                        display="flex"
                        flexDirection="column"
                        variant="body5"
                        color="marron"
                        sx={{
                          "& ol": {
                            paddingLeft: {
                              xs: pxToVw(16, MOBILE_BASE),
                              md: pxToVw(16),
                            },
                            margin: 0,
                          },
                          "& li": {
                            paddingLeft: {
                              xs: pxToVw(8, MOBILE_BASE),
                              md: pxToVw(5),
                            },
                          },
                        }}
                        whiteSpace={"pre-wrap"}
                        dangerouslySetInnerHTML={{ __html: faq.description }}
                      />
                    </Collapse>
                  )}
                </Grid>
                <Grid size={{ xs: 2, md: 1 }} container justifyContent="flex-end">
                  <Typography
                    variant="label1"
                    color="marron"
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {isExpanded ? "↑" : "↓"}
                  </Typography>
                </Grid>
                {faq.description && isMobile && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Grid container>
                      <Typography
                        variant="body4"
                        color="marron"
                        mt={{ xs: pxToVw(0, MOBILE_BASE), md: pxToVw(16) }}
                        sx={{
                          "& ol": {
                            paddingLeft: {
                              xs: pxToVw(16, MOBILE_BASE),
                              md: pxToVw(16),
                            },
                            margin: 0,
                          },
                          "& li": {
                            paddingLeft: {
                              xs: pxToVw(5, MOBILE_BASE),
                              md: pxToVw(5),
                            },
                          },
                        }}
                        whiteSpace={"pre-wrap"}
                        dangerouslySetInnerHTML={{ __html: faq.description }}
                      />
                    </Grid>
                  </Collapse>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </>
  );
}
